import React from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaExclamationTriangle,
  FaHubspot,
} from "react-icons/fa";
import {
  formatDate,
  formatNumber,
  hubspotCompanyUrl,
  hubspotDealUrl,
  isPilotOverdue,
  playbookProgress,
} from "../selectors";
import { ClientOverview } from "./ClientOverview";

const COLUMN_COUNT = 9;

function PlaybookCell({ pilot }) {
  const progress = playbookProgress(pilot);

  return (
    <div className="pl-playbook">
      <span className="pl-playbook-label">{progress.label}</span>
      {progress.value === null ? null : (
        <span
          className="pl-track"
          role="progressbar"
          aria-label={`${pilot.name} playbook progress`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress.value * 100)}
        >
          <span
            className="pl-track-fill"
            style={{ width: `${Math.round(progress.value * 100)}%` }}
          />
        </span>
      )}
    </div>
  );
}

function FeatureChips({ features, limit }) {
  if (!features?.length) {
    return <span className="pl-muted">None in notes</span>;
  }
  const shown = limit ? features.slice(0, limit) : features;
  const hidden = features.length - shown.length;

  return (
    <span className="pl-chips">
      {shown.map((feature) => (
        <span key={feature.key} className="pl-feature">
          {feature.label}
        </span>
      ))}
      {hidden > 0 ? <span className="pl-feature pl-feature--more">+{hidden}</span> : null}
    </span>
  );
}

/** Active client users over the last 7 days, or why it is unknown. */
function UsageCell({ pilot }) {
  if (!pilot.organizationId) {
    return (
      <span className="pl-muted" title="No Addy account matches the deal's contacts">
        Not linked
      </span>
    );
  }
  if (!pilot.recentUsage) {
    return <span className="pl-muted">—</span>;
  }
  if (pilot.recentUsage.activeUsers === 0) {
    return <span className="pl-badge pl-badge--bad">Nobody</span>;
  }

  return formatNumber(pilot.recentUsage.activeUsers);
}

function PilotDetail({ pilot }) {
  const facts = [
    ["Deal stage", pilot.stage],
    ["Playbook step", pilot.subStage?.label],
    ["Step last changed", formatDate(pilot.subStageChangedAt)],
    ["Entered pilot stage", formatDate(pilot.pilotStartedAt)],
    ["Expected close", formatDate(pilot.closeDate)],
    ["Company type", pilot.companyType],
    ["LOS", pilot.los],
    ["Loan officers (company)", formatNumber(pilot.loanOfficers)],
    ["Priority", pilot.priority],
    ["Last HubSpot activity", formatDate(pilot.lastActivityAt)],
  ].filter(([, value]) => value && value !== "—");

  return (
    <div className="pl-detail">
      <div className="pl-detail-main">
        <div className="pl-detail-links">
          <a
            className="pl-link"
            href={hubspotDealUrl(pilot.dealId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaHubspot aria-hidden="true" /> Deal in HubSpot
          </a>
          {pilot.companyId ? (
            <a
              className="pl-link"
              href={hubspotCompanyUrl(pilot.companyId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaHubspot aria-hidden="true" /> Company in HubSpot
            </a>
          ) : null}
        </div>
        <dl className="pl-facts">
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <div className="pl-detail-block">
          <h4>Features mentioned in HubSpot notes</h4>
          <FeatureChips features={pilot.features} />
        </div>
        {pilot.nextStep ? (
          <div className="pl-detail-block">
            <h4>Next step</h4>
            <p>{pilot.nextStep}</p>
          </div>
        ) : null}
        {!pilot.isInPilotStage ? (
          <p className="pl-note">
            HubSpot still has this deal in “{pilot.stage}”. It is listed because
            its playbook step is a pilot step.
          </p>
        ) : null}
        {pilot.organizationId ? (
          <ClientOverview organizationId={pilot.organizationId} />
        ) : (
          <p className="pl-note">
            No Addy account matches this deal's contacts, so there is no usage to
            show. Add the pilot users as contacts on the deal in HubSpot.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Running pilots. A row opens to show everything HubSpot knows about the pilot
 * and how the client is using Addy.
 */
export function PilotsTable({
  pilots,
  loading,
  expandedId,
  onToggle,
  emptyMessage = "No pilots are running in HubSpot right now.",
}) {
  if (!loading && !pilots.length) {
    return <div className="pl-empty">{emptyMessage}</div>;
  }

  return (
    <div className="pl-table-wrap">
      <table className="pl-table">
        <thead>
          <tr>
            <th className="pl-th-toggle" aria-label="Expand" />
            <th className="pl-left">Deal</th>
            <th className="pl-left">Playbook</th>
            <th className="pl-left">Owner</th>
            <th className="pl-left">FDE</th>
            <th className="pl-left">Features</th>
            <th className="pl-left">LOS</th>
            <th className="pl-th-wrap" title="Client people who used Addy in the last 7 days">
              Users this week
            </th>
            <th className="pl-th-wrap">Days in Pilot stage</th>
          </tr>
        </thead>
        <tbody>
          {loading && !pilots.length
            ? Array.from({ length: 4 }).map((_, rowIndex) => (
                <tr key={rowIndex}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cellIndex) => (
                    <td key={cellIndex}>
                      <span className="pl-skel" />
                    </td>
                  ))}
                </tr>
              ))
            : null}
          {pilots.map((pilot) => {
            const isOpen = expandedId === pilot.dealId;
            const overdue = isPilotOverdue(pilot);

            return (
              <React.Fragment key={pilot.dealId}>
                <tr className={`pl-row ${isOpen ? "is-open" : ""}`}>
                  <td className="pl-td-toggle">
                    <button
                      type="button"
                      className="pl-row-toggle"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Hide" : "Show"} details for ${pilot.name}`}
                      onClick={() => onToggle(pilot.dealId)}
                    >
                      {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                    </button>
                  </td>
                  <td className="pl-left">
                    <span className="pl-deal-name">{pilot.name}</span>
                    <span className="pl-deal-sub">
                      {[pilot.stage, pilot.companyType].filter(Boolean).join(" · ")}
                    </span>
                  </td>
                  <td className="pl-left">
                    <PlaybookCell pilot={pilot} />
                  </td>
                  <td className="pl-left">{pilot.owner || <span className="pl-muted">—</span>}</td>
                  <td className="pl-left">
                    {pilot.fde || <span className="pl-badge pl-badge--warn">Unassigned</span>}
                  </td>
                  <td className="pl-left pl-wrap">
                    <FeatureChips features={pilot.features} limit={2} />
                  </td>
                  <td className="pl-left pl-los">{pilot.los || <span className="pl-muted">—</span>}</td>
                  <td>
                    <UsageCell pilot={pilot} />
                  </td>
                  <td>
                    {overdue ? (
                      <span className="pl-badge pl-badge--warn" title="Past the 3-week playbook">
                        <FaExclamationTriangle aria-hidden="true" />{" "}
                        {formatNumber(pilot.daysInPilot)}
                        <span className="pl-sr-only"> days, past the 3-week playbook</span>
                      </span>
                    ) : (
                      formatNumber(pilot.daysInPilot)
                    )}
                  </td>
                </tr>
                {isOpen ? (
                  <tr className="pl-detail-row">
                    <td colSpan={COLUMN_COUNT}>
                      <PilotDetail pilot={pilot} />
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
